<script setup >

import TotalCount from "@/components/common/totalCount/totalCount.vue";
import {computed, onMounted, ref, watch} from "vue";
import PagePerItems from "@/components/common/paging/pagePerItems.vue";
import {useLanguageStore} from "@/pinia";
import PagingComponent from "@/components/common/paging/pagingComponent.vue";
import {useToast} from "@/composables/useToast";

const toast = useToast();
import AddButton from "@/components/common/button/addButton.vue";
import DeleteButton from "@/components/common/button/deleteButton.vue";
import SearchIconComponent from "@/components/common/button/searchAndResetButton.vue";
import UserInsertModal from "@/components/modal/user/userInsertModal.vue";
import UserDetailModal from "@/components/modal/user/userDetailModal.vue";
import UserEditModal from "@/components/modal/user/userEditModal.vue";
import axios from "@/axios";

const emits = defineEmits(['set_title'])
const languageStore = useLanguageStore();

// Role display helper
const getRoleDisplay = (role) => {
  const roleMap = {
    'ADMIN': languageStore.get('admin'),
    'EDITOR': languageStore.get('editor'),
    'USER': languageStore.get('user')
  };
  return roleMap[role] || role || '-';
};
const title = computed(() => languageStore.get('userManagement'));
const totalCount = ref(0);
const pageSize = ref(10);
const currentPage = ref(1);
const totalPage = ref(0);
const userLists = ref([]);
const isAddUserModal = ref(false);
const isDetailUserModal = ref(false);
const isEditUserModal = ref(false);
const selectUser = ref('');
const detailUserId = ref('');
const editUserData = ref(null);
const searchUsername = ref('');
const searchDepartmentId = ref('');

const set_currentPage= (page)=> {
  currentPage.value = page
  getData();
}
const set_pageSize = (size)=>{
  currentPage.value= 1;
  pageSize.value= size;
  getData();
}
const getData = ()=>{
  const params = {
    page: currentPage.value - 1,
    size: pageSize.value
  }

  if(searchUsername.value) {
    params.username = searchUsername.value;
  }
  if(searchDepartmentId.value) {
    params.departmentId = searchDepartmentId.value;
  }

  axios.get('/api/users', { params })
      .then((response) => {
        console.log('User data response:', response.data);
        userLists.value = response.data.dataList || [];
        totalPage.value = response.data.page || 0;
        totalCount.value = response.data.total || 0;
      })
      .catch((error) => {
        console.error('Failed to fetch users:', error);
        userLists.value = [];
        totalPage.value = 0;
        totalCount.value = 0;
      });
}
const set_isAddUserModal = (is) => {
  isAddUserModal.value = is;
  if(!is) {
    getData();
  }
}

const fn_saveUser = (newUser) => {
  axios.post('/api/users', newUser, {headers: { 'Content-Type': 'application/json' }})
      .then((response) => {
        console.log('User created:', response.data);
        toast.success(languageStore.get('userCreated'));
        getData();
      })
      .catch((error) => {
        console.error('Failed to create user:', error);
        if(error.response && error.response.data && error.response.data.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error(languageStore.get('userCreationFailed'));
        }
      });
}
const fn_deleteUser = ()=>{
  if(!selectUser.value) {
    toast.warning(languageStore.get('noUserSelected'));
    return;
  }

  if(confirm(languageStore.get('deleteConfirm'))) {
    axios.delete(`/api/users/${selectUser.value}`)
      .then(() => {
        toast.success(languageStore.get('deleteSuccess'));
        selectUser.value = '';
        getData();
      })
      .catch((error) => {
        console.error('Failed to delete user:', error);
        toast.error(languageStore.get('deleteFailed'));
      });
  }
}

const selectedUser = (userId) => {
  selectUser.value = userId;
}

const openUserDetail = (userId) => {
  detailUserId.value = userId;
  isDetailUserModal.value = true;
}

const set_isDetailUserModal = (is) => {
  isDetailUserModal.value = is;
  if(!is) {
    detailUserId.value = '';
  }
}

const openEditModal = (userData) => {
  editUserData.value = userData;
  isEditUserModal.value = true;
}

const set_isEditUserModal = (is) => {
  isEditUserModal.value = is;
  if(!is) {
    editUserData.value = null;
  }
}

const fn_search = () => {
  currentPage.value = 1;
  getData();
}

const fn_reset = () => {
  searchUsername.value = '';
  searchDepartmentId.value = '';
  currentPage.value = 1;
  getData();
}

onMounted(()=>{
  getData();
  emits('set_title', title.value);
})
watch(title, (v) => emits('set_title', v))
</script>

<template>
  <v-snackbar v-model="toast.show.value" :color="toast.color.value" :timeout="toast.timeout.value" location="top">
    {{ toast.message.value }}
    <template v-slot:actions>
      <v-btn variant="text" @click="toast.close()">{{ languageStore.get('close') }}</v-btn>
    </template>
  </v-snackbar>
  <div class="searchArea">
    <v-text-field
        class="search-field"
        type="text"
        variant="outlined"
        density="compact"
        :label="languageStore.get('userName')"
        hide-details
        v-model="searchUsername"
        @keyup.enter="fn_search"
    ></v-text-field>
    <v-text-field
        class="search-field"
        type="text"
        variant="outlined"
        density="compact"
        :label="languageStore.get('departmentId')"
        hide-details
        v-model="searchDepartmentId"
        @keyup.enter="fn_search"
    ></v-text-field>
    <search-icon-component
        @fn_search="fn_search"
        @fn_reset="fn_reset"
    ></search-icon-component>
  </div>
  <div class="mainContentArea">
    <div class="contentTopArea">
      <total-count :total-count="totalCount"></total-count>
      <div class="contentTopRight">
        <page-per-items
            :page-per-items="pageSize"
            @set_pageSize="set_pageSize"
        ></page-per-items>
        <div class="btnArea">
          <add-button divide="user" @click="set_isAddUserModal(true)"></add-button>
          <delete-button @click="fn_deleteUser"></delete-button>
        </div>
      </div>
    </div>

    <div class="contentData">
      <div class="contentHead">
        <div>No.</div>
        <div>{{ languageStore.get('userId') }}</div>
        <div>{{ languageStore.get('name') }}</div>
        <div>{{ languageStore.get('email') }}</div>
        <div>{{ languageStore.get('phoneNumber') }}</div>
        <div>{{ languageStore.get('role') }}</div>
      </div>
      <div class="contentBody" id="contentBody">
        <template v-if="userLists.length > 0 ">
          <div
              v-for="(user, index) in userLists"
              :key="user.userId"
              :class="{'selectedUserList': selectUser == user.userId, 'userList': selectUser != user.userId}"
              @click="selectedUser(user.userId)"
              @dblclick="openUserDetail(user.userId)"
          >
            <div>{{ (currentPage - 1) * pageSize + index + 1 }}</div>
            <div>{{ user.userId }}</div>
            <div>{{ user.name }}</div>
            <div>{{ user.email }}</div>
            <div>{{ user.phone }}</div>
            <div>
              <v-chip
                  :color="user.role === 'ADMIN' ? 'error' : user.role === 'EDITOR' ? 'warning' : 'info'"
                  size="small"
                  variant="flat"
              >
                {{ getRoleDisplay(user.role) }}
              </v-chip>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="noData">
            <v-icon size="36" color="#B0BEC5">mdi-database-off-outline</v-icon>
            <p>{{languageStore.get('noData')}}</p>
          </div>
        </template>
      </div>
    </div>

  </div>
  <div class="pagingArea">
    <paging-component
        @set_currentPage="set_currentPage"
        :page="currentPage"
        :total-page=totalPage
    ></paging-component>
  </div>
  <user-insert-modal
      :isUserInsertModal="isAddUserModal"
      @set_isUserInsertModal="set_isAddUserModal"
      @fn_saveUser="fn_saveUser"
  ></user-insert-modal>
  <user-detail-modal
      :isUserDetailModal="isDetailUserModal"
      :userId="detailUserId"
      @set_isUserDetailModal="set_isDetailUserModal"
      @openEdit="openEditModal"
  ></user-detail-modal>
  <user-edit-modal
      :isUserEditModal="isEditUserModal"
      :userData="editUserData"
      @set_isUserEditModal="set_isEditUserModal"
      @refresh="getData"
  ></user-edit-modal>
</template>

<style scoped>
.contentHead{
  display: grid;
  grid-template-columns: 4vw 10vw 10vw 16vw 10vw 10vw;
  background: #052C65;
  color: white;
  height: 4vh;
  align-items: center;
  font-weight: bold;
}
.selectedUserList div{
  display: flex;
  align-items: center;
  justify-content: center;
}
.contentHead div{
  display: flex;
  align-items: center;
  justify-content: center;
}
.contentTopRight{
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.mainContentArea{
  background: white;
  width: 70vw;
  height: 70vh;
  border-radius: 5px;
  overflow-y: auto;
  overflow-x: hidden;
}

.searchArea{
  background: white;
  position: relative;
  width: 70vw;
  height: 8vh;
  margin: 10px 0 10px 0;
  padding: 0 20px;
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
.search-field {
  min-width: 150px;
  max-width: 200px;
}

.searchUsernameArea{
  display: flex;
  align-items: center;
}

.searchDepartmentArea{
  display: flex;
  align-items: center;
}

.contentTopArea{
  display: flex;
  justify-content: space-between;
  height: 6vh;
  align-items: center;
  padding: 1px 10px 0px 10px;
}
.btnArea{
  display: flex;
  min-width: 10vw;
  justify-content: space-around;
}
.btnArea button{
  white-space: nowrap;
  padding: 0 12px;
}

.noData {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #90A4AE;
  font-size: 14px;
  gap: 8px;
}

.userList:hover{
  background: #E0F0FF;
}
.userList div{
  display: flex;
  align-items: center;
  justify-content: center;
}
.selectedUserList{
  display: grid;
  grid-template-columns: 4vw 10vw 10vw 16vw 10vw 10vw;
  align-items: center;
  height: 6vh;
  border-bottom: 1px solid #C7C7C7;
  background: #E0F0FF;
  font-size: 14px;
}
.userList{
  display: grid;
  grid-template-columns: 4vw 10vw 10vw 16vw 10vw 10vw;
  align-items: center;
  height: 6vh;
  border-bottom: 1px solid #C7C7C7;
  cursor: pointer;
  font-size: 14px;
}
.pagingArea{
  display: flex;
  justify-content: center;
}
</style>